import {
  ActionFilter,
  PaginationParams,
  EnhancedActionLog,
  PaginatedResponse,
  KeywordState,
  GroupedActionsResponse,
  CampaignGroup
} from '@/types/action-types';
import { ActionType, ActionLog } from '@/lib/types';
import fetchWithAuth from '../apis';
import { apiEndpoint } from '../consts';

/**
 * Type for undo action options
 */
export interface UndoActionOptions {
  keywordId?: number;
  keywordText?: string; 
  adId?: number;
  originalActionId?: number;
}

/**
 * API response for undo action
 */
export interface UndoActionResponse {
  success: boolean;
  message: string;
  result?: {
    keyword_id?: number;
    status?: string;
    database_updated?: boolean;
    google_ads_updated?: boolean;
    google_ads_error?: string;
    google_ads_details?: string;
    [key: string]: unknown;
  };
  partialSuccess?: boolean;
}

/**
 * Error handler for API responses
 */
const handleApiError = (error: unknown): never => {
  console.error('API Error:', error);
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const apiError = error as { response: { status: number; statusText: string } };
    throw new Error(`API Error: ${apiError.response.status} - ${apiError.response.statusText}`);
  } else if (typeof error === 'object' && error !== null && 'request' in error) {
    throw new Error('Network Error: No response received from server');
  } else {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Error: ${errorMessage}`);
  }
};

/**
 * Build URL query parameters from filters and pagination
 */
const buildQueryParams = (params: Partial<ActionFilter & PaginationParams & { campaign_id?: number; organization_id?: number }>): URLSearchParams => {
  const queryParams = new URLSearchParams();

  // Add pagination params
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
  if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());

  // Add filter params
  if (params.action_type) queryParams.append('action_type', params.action_type);
  if (params.entity_type) queryParams.append('entity_type', params.entity_type);
  if (params.is_undone !== undefined) queryParams.append('is_undone', params.is_undone.toString());
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.id !== undefined) queryParams.append('id', params.id.toString());
  if (params.campaign_id !== undefined) queryParams.append('campaign_id', params.campaign_id.toString());
  if (params.organization_id !== undefined) queryParams.append('organization_id', params.organization_id.toString());

  return queryParams;
};

/**
 * Raw grouped campaign data from backend API
 */
interface RawGroupedCampaignItem {
  id: string;
  campaign_id: number;
  campaign_name: string;
  action_type: string;
  task_count: number;
  unique_keyword_count: number;
  created_at: string;
  is_undone: boolean;
  user_name?: string;
  description?: string;
  ad_actions?: Array<{
    entity_id: number;
    action_type: string;
    count: number;
    keywords: Array<{ text: string; match_type: string } | string>;
  }>;
  keywords?: Array<{ text: string; match_type: string } | string>;
  tasks?: unknown[];
  unique_keywords?: Map<string, unknown>;
  budget_before_value?: number;
  budget_after_value?: number;
  has_budget_action?: boolean;
}

/**
 * Raw deleted keyword data from API
 */
interface RawDeletedKeyword {
  id?: number;
  text?: string;
  match_type?: string;
  keyword_id?: number;
  keyword_text?: string;
  reason?: string;
  performance_metrics?: {
    impressions: number;
    clicks: number;
    cost: number;
    ctr: number;
    conversions?: number;
  };
  state?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Raw API response type before mapping
 */
interface RawActionLogResponse {
  id: number;
  campaign_id: number;
  campaign_name: string;
  action_type: string;
  created_at: string;
  is_undone: boolean;
  undone_at?: string;
  recommendation_ids?: number[];
  entity_id?: number;
  entity_type?: string;
  user_id?: number;
  user_name?: string;
  log?: string;
  reason?: string;
  campaign?: {
    id?: number;
    name?: string;
    slug?: string;
  };
  before_state?: Record<string, unknown>;
  after_state?: Record<string, unknown>;
  details?: {
    reason?: string;
    log?: string;
    deleted_keywords?: RawDeletedKeyword[];
    [key: string]: unknown;
  };
  campaign_slug?: string;
  ad_url?: string;
  ad_slug?: string;
}

/**
 * Map API response to EnhancedActionLog
 */
const mapToEnhancedActionLog = (item: RawActionLogResponse): EnhancedActionLog => {
  const campaign = item.campaign || {};
  const beforeState = item.before_state as KeywordState | undefined;
  const afterState = item.after_state as KeywordState | undefined;
  
  // Handle deleted keywords transformation if needed
  let deletedKeywords: KeywordState[] = [];
  if (item.details?.deleted_keywords && Array.isArray(item.details.deleted_keywords)) {
    deletedKeywords = item.details.deleted_keywords.map((kw) => ({
      id: kw.id || 0,
      ad_id: 0, // Required by KeywordState interface
      text: kw.text || '',
      match_type: kw.match_type || 'BROAD',
      keyword_id: kw.keyword_id || kw.id,
      keyword_text: kw.keyword_text || kw.text,
      reason: kw.reason,
      performance_metrics: kw.performance_metrics,
      ...kw.state
    } as KeywordState));
  }
  
  // Return the enhanced action log with consistent structure
  return {
    id: item.id,
    campaign_id: item.campaign_id,
    campaign_name: campaign.name || `Campaign ${item.campaign_id}`,
    action_type: item.action_type as ActionType,
    created_at: item.created_at,
    is_undone: item.is_undone,
    undone_at: item.undone_at,
    entity_id: item.entity_id,
    entity_type: item.entity_type,
    recommendation_ids: item.recommendation_ids,
    user_id: item.user_id,
    user_name: item.user_name,
    campaign: {
      id: campaign.id || item.campaign_id,
      name: campaign.name,
      slug: campaign.slug
    },
    campaign_slug: campaign.slug,
    before_state: beforeState as KeywordState | undefined,
    after_state: afterState as KeywordState | undefined,
    details: {
      ...item.details,
      reason: item.reason,
      log: item.log,
      deleted_keywords: deletedKeywords
    },
    log: item.log,
    reason: item.reason
  };
};

/**
 * Standardized API client for action-related operations
 */
/**
 * Bulk pause multiple keywords in a campaign
 * 
 * @param campaignId The ID of the campaign
 * @param keywordIds List of keyword IDs to pause
 * @param reason Optional reason for pausing keywords
 * @returns API response with operation details
 */
export const bulkPauseKeywords = async (
  campaignId: number,
  keywordIds: number[],
  reason?: string
): Promise<{
  success: boolean;
  message: string;
  recommendation_ids: number[];
  details: {
    paused_count: number;
    operation_time: number;
    api_calls: number;
    invalid_keyword_ids: number[];
    bulk_operation_id: number;
  }
}> => {
  try {
    const response = await fetchWithAuth(
      `${apiEndpoint}/campaigns/${campaignId}/keywords/bulk-pause`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keyword_ids: keywordIds,
          reason
        })
      }
    );

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error);
  }
};

export const actionApi = {
  /**
   * Fetch campaign actions with pagination and filtering
   */
  getCampaignActions: async (
    campaignId: number,
    params: Partial<ActionFilter & PaginationParams> = { skip: 0, limit: 10 }
  ): Promise<PaginatedResponse<EnhancedActionLog>> => {
    try {
      const queryParams = buildQueryParams(params);
      const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/actions?${queryParams.toString()}`);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      
      return {
        ...data,
        items: data.items.map(mapToEnhancedActionLog)
      };
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  /**
   * Get details of a specific action by ID
   *
   * Uses the /actions/all endpoint with filtering to avoid relying on a missing endpoint
   */
  getActionDetails: async (actionId: number): Promise<EnhancedActionLog> => {
    try {
      // Use the existing all actions endpoint with filtering
      const queryParams = new URLSearchParams();
      queryParams.append('id', actionId.toString());
      queryParams.append('limit', '1');

      const response = await fetchWithAuth(`${apiEndpoint}/actions/all?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();

      // If the action was found, return it
      if (data.items && data.items.length > 0) {
        return mapToEnhancedActionLog(data.items[0]);
      }

      throw new Error(`Action with ID ${actionId} not found`);
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // getAllActions was deleted (#6448): cross-org aggregation must not exist
  // client-side. Single-action lookups go through getActionDetails, which the
  // backend scopes to the caller's active organization.

  /**
   * Undo an action with optional keyword parameters
   *
   * @returns Enhanced response with details about partial successes
   */
  undoAction: async (
    actionId: number,
    options?: { params?: UndoActionOptions }
  ): Promise<UndoActionResponse> => {
    try {
      // If we have specific keyword options, send them in the request body
      const body = options?.params ? JSON.stringify(options.params) : undefined;

      const response = await fetchWithAuth(
        `${apiEndpoint}/actions/${actionId}/undo`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body
        }
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();

      // Check for partial success scenarios
      let message = data.message;
      if (data.success && data.result) {
        // For keywords - check if Google Ads update succeeded
        if (data.result.google_ads_updated === false && data.result.database_updated === true) {
          // Format a user-friendly message
          message = "Keyword was restored in our system but could not be reactivated in Google Ads. " +
                   "This can happen if the keyword was previously deleted from your Google Ads account.";
        }
      }

      return {
        ...data,
        message,
        partialSuccess: data.success && data.result?.google_ads_updated === false
      };
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  /**
   * Get actions by recommendation IDs
   */
  getActionsByRecommendations: async (
    recommendationIds: number[],
    campaignId: number
  ): Promise<EnhancedActionLog[]> => {
    try {
      const queryParams = new URLSearchParams();
      recommendationIds.forEach(id => queryParams.append('recommendation_ids', id.toString()));
      
      const response = await fetchWithAuth(
        `${apiEndpoint}/campaigns/${campaignId}/actions/by-recommendations?${queryParams.toString()}`
      );
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      return (data.items || []).map(mapToEnhancedActionLog);
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  // getActionsByEntity was deleted (#6448): the /actions/entity/{type}/{id}
  // route does not exist on the backend and nothing called this client.

  /**
   * Get grouped actions with consistent keyword counting
   * 
   * @param params Query parameters for filtering and pagination
   */
  getGroupedActions: async (
    params: Partial<ActionFilter & PaginationParams & { campaign_id?: number; organization_id?: number }> = { skip: 0, limit: 100 }
  ): Promise<GroupedActionsResponse> => {
    try {
      const queryParams = buildQueryParams(params);
      const url = `${apiEndpoint}/actions/grouped?${queryParams.toString()}`;
      
      // campaign_id is now handled as part of the params object
      
      const response = await fetchWithAuth(url);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json() as {
        items: RawGroupedCampaignItem[];
        total: number;
        page: number;
        page_size: number;
        campaign?: {
          id: number | null;
          name: string;
          slug: string;
        };
      };

      const toKeywordRef = (k: { text: string; match_type: string } | string) =>
        typeof k === 'string' ? { text: k, match_type: 'BROAD' } : k;

      const transformedItems = data.items.map((item): CampaignGroup => ({
        id: item.id,
        campaignId: item.campaign_id,
        campaignName: item.campaign_name,
        actionType: item.action_type,
        taskCount: item.task_count,
        uniqueKeywordCount: item.unique_keyword_count,
        createdAt: item.created_at,
        isUndone: item.is_undone,
        userName: item.user_name,
        description: item.description,
        adActions: (item.ad_actions || []).map(a => ({
          ...a,
          keywords: a.keywords.map(toKeywordRef),
        })),
        keywords: (item.keywords || []).map(toKeywordRef),
        tasks: (item.tasks || []) as ActionLog[],
        uniqueKeywords: new Map<string, KeywordState>(),
        budgetBeforeValue: item.budget_before_value,
        budgetAfterValue: item.budget_after_value,
        hasBudgetAction: item.has_budget_action
      }));
      
      return {
        items: transformedItems,
        total: data.total,
        page: data.page,
        page_size: data.page_size,
        campaign: data.campaign
      };
    } catch (error) {
      return handleApiError(error);
    }
  }
};